import type { FlexProps } from '@chakra-ui/react';
import { Box, Flex, Icon, Skeleton, Text } from '@chakra-ui/react';
import { useStakedNfts } from '@ui/hooks/useCruisers';
import { useTranslations } from '@ui/i18n';
import useStakeNftInfo from '@ui/views/Garden/GardenViewGrowHelpers/GardenStakeNftDrawer/useStakeNftInfo';
import { inDefaultSelector } from '@veno-app/chakra';
import { Helper } from '@veno-app/icons';
import React from 'react';

import { useGardenModalStore } from '../useGardenModalStore';
import type { GardenBoostCardProps } from './GardenBoostCard';
import { GardenBoostCard } from './GardenBoostCard';
import GardenBoostMoreDetail from './GardenBoostMoreDetail';

function GardenHelperBoostCard(
  props: Omit<GardenBoostCardProps, 'avatarText'>,
) {
  const gardenType = useGardenModalStore((s) => s.type);
  const { data: stakedCruisers } = useStakedNfts(gardenType);
  const nftTotalBoost = stakedCruisers?.reduce(
    (prev, current) => prev + (current.multiplier ?? 0),
    0,
  );

  return (
    <GardenBoostCard
      isExpandable
      avatarText={
        nftTotalBoost !== undefined ? `+${nftTotalBoost.toFixed(2)}x` : ''
      }
      {...props}
    >
      <Flex
        justify="center"
        align="center"
        position="absolute"
        w="full"
        h="full"
      >
        <Icon
          as={Helper}
          w="22px"
          h="35px"
          color="garden-light"
          _dark={{
            color: 'brand-blue',
            opacity: 0.4,
          }}
          sx={{
            [inDefaultSelector]: {
              color: 'brand-white',
            },
          }}
        />
      </Flex>
      <GardenHelperBoostCardBody />
      <GardenBoostMoreDetail
        mt={{
          base: '16px',
          desktop: '10px',
        }}
      />
    </GardenBoostCard>
  );
}

const GardenHelperBoostCardBody = ({ ...props }: FlexProps) => {
  const t = useTranslations();
  const { maxNForStake, numberOfStakedCruisers } = useStakeNftInfo();

  return (
    <Flex flexDir="column" flex={1} {...props}>
      <Text
        textStyle={{
          base: 'bodySmallBold',
          desktop: 'h4Bold',
        }}
      >
        {t('Active helper boost')}
      </Text>
      <Flex mt={2} ml="16.67%" justify="space-between">
        {Array.from({ length: 5 }).map((_, i) => (
          <Icon
            as={Helper}
            key={i}
            w="9px"
            color={
              i < (numberOfStakedCruisers ?? 0) ? 'garden.dark' : '#E9F9CC'
            }
            _dark={{
              color:
                i < (numberOfStakedCruisers ?? 0)
                  ? 'brand-blue'
                  : 'brand-atomLight',
            }}
            sx={{
              [inDefaultSelector]: {
                color:
                  i < (numberOfStakedCruisers ?? 0)
                    ? 'brand-shadow'
                    : 'brand-white',
              },
            }}
          />
        ))}
      </Flex>
      <Box
        mt={1.5}
        w="full"
        rounded="6px"
        bg="bg.main"
        shadow="smallCard.active"
        h="11px"
      >
        {numberOfStakedCruisers !== undefined && maxNForStake !== undefined && (
          <Box
            h="full"
            w={`calc(${(100 * numberOfStakedCruisers) / 5}%)`}
            maxW="100%"
            rounded="13px"
            bg="garden.dark"
            _dark={{
              bg: 'brand-blue',
            }}
            sx={{
              [inDefaultSelector]: {
                bg: 'brand-shadow',
              },
            }}
          />
        )}
      </Box>
      <Skeleton isLoaded={maxNForStake !== undefined} mt={1.5}>
        <Text
          flex="1"
          color="text.light"
          textStyle={{
            base: 'caption',
            desktop: 'body',
          }}
        >
          {maxNForStake !== numberOfStakedCruisers && (
            <>
              {t('You can employ {num} more helper', {
                num: maxNForStake ?? 0,
              })}
            </>
          )}
          {maxNForStake !== undefined &&
            maxNForStake === numberOfStakedCruisers && (
              <>{t('Max number of helpers employed')}</>
            )}
        </Text>
      </Skeleton>
    </Flex>
  );
};

export default GardenHelperBoostCard;
